import React from 'react';
import styled from 'styled-components';
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { threePracticaLocales } from '../../locales/firstScreen';
import { useSwipeable } from 'react-swipeable';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

const slideVariants = {
  enter: {
    opacity: 0,
  },
  center: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
};

const transition = {
  duration: 0.3,
  ease: 'easeInOut',
};

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  position: relative;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 0 10px;
  padding-bottom: 100px;
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 42px;
  margin-bottom: 15px;
  margin-top: min(1px, 0.2vh);
  line-height: 80%;
  font-weight: 600;
  text-align: center;
`;

const SectionText = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 0 20px 32px; // Увеличиваем нижний отступ
  line-height: 145%;
  white-space: pre-line;

  @media (min-height: 800px) {
    margin-bottom: 40px;
  }

  @media (max-height: 700px) {
    margin-bottom: 24px;
    font-size: 12px;
  }

  @media (max-height: 600px) {
    margin-bottom: 16px;
    font-size: 11px;
  }
`;

const PracticaButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px; /* уменьшен вертикальный padding */
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%; /* добавлен line-height для компактности */
`;

const SwapContainer = styled.div`
  background: #ffffff0d;
  padding: 0 0 40px;
  border-radius: 12px;
  margin: auto 0 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: auto;
  width: 100%;
`;

const SwapIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1px 0;
  position: relative;

  &::before {
    // Изменено с after на before, чтобы линия была под иконкой
    content: '';
    position: absolute;
    top: 50%; // Центрируем вертикально
    left: 0px;
    right: 0px;
    height: 1px;
    background-color: #303030;
    z-index: 0; // Помещаем линию под иконку
  }

  img {
    position: relative;
    z-index: 1; // Поднимаем иконку над линией
  }
`;

const SwapInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px;

  @media (max-height: 667px) {
    gap: 0;
  }
`;

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const SwapRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 0;
  gap: 12px;
`;

const TokenLabel = styled.span`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
`;

const BalanceText = styled.span`
  color: rgb(10, 149, 255);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const SwapRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  padding: 12px 0 20px;
  width: 100%;
  gap: 12px;
`;

const SwapValue = styled.span`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
`;

const TokenLogo = styled.p`
  width: 30px;
  height: 30px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: rgba(71, 114, 255, 1);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 100%;
  font-weight: 500;
  color: rgba(35, 35, 35, 1);
`;

const InputButton = styled.button<{ isDisabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 21px;
  margin: 0 10px 0;
  border-radius: 12px;
  border: 2px solid
    ${(props) =>
      props.isDisabled
        ? 'rgba(180, 180, 180, 0.1)'
        : 'rgba(180, 180, 180, 0.2)'};
  background: none;
  color: ${(props) =>
    props.isDisabled ? 'rgba(255, 255, 255, 0.2)' : '#FFFFFF'};
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};

  @media (max-height: 700px) {
    padding: 15px 18px;
  }

  @media (max-height: 600px) {
    padding: 12px 15px;
  }
`;
const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  position: relative;
  text-align: center;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 26px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 90%;
  white-space: pre-line;
  letter-spacing: -0.02em;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin: 50px 0 30px;
  padding: 13px;
  border-radius: 20px;
  justify-content: center;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #232323;
  width: 100%;
  max-width: 380px;
  min-height: 223px;
  border-radius: 20px;
  padding: 16px;
  margin: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorModalContent = styled(ModalContent)`
  background: #151515;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 20px;
`;

const SuccessModalContent = styled(ModalContent)`
  background: #7354f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 10px;
  min-height: 210px;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0 0 16px 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px 15px;
  margin-top: 20px;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: none;
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  text-transform: uppercase;
`;

const Input = styled.input<{ isZero: boolean }>`
  background: none;
  border: none;
  color: ${(props) => (props.isZero ? '#666666' : '#FFFFFF')};
  font-size: 18px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  outline: none;
  text-align: right;
  padding: 0;
  flex: 1;
  min-width: 0;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

interface ThreePractica2Props {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onNavigateToThreeScreen?: () => void;
  onNavigateToPractica3?: () => void;
  toHome?: () => void;
}

const ThreePractica2: React.FC<ThreePractica2Props> = ({
  onNavigateToPractica3,
  onSwipeRight,
  toHome,
}) => {
  const [sendValue, setSendValue] = useState('');
  const [receiveValue, setReceiveValue] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const sendInputRef = useRef<HTMLInputElement>(null);
  const receiveInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const exchangeRate = 75.357653; // Коэффициент обмена: 1 Token C = 71 Token D

  const handleSendValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Ограничиваем длину строки до 6 символов
    if (value.length > 6) return;

    const numValue = Number(value);

    if (numValue < 0) {
      setShowErrorModal(true);
      return;
    }

    if (numValue > 1000) {
      setShowErrorModal(true);
      setSendValue('1000');
      setReceiveValue(String(1000 * exchangeRate));
      return;
    }

    setSendValue(value);
    setReceiveValue(value ? String(numValue * exchangeRate) : '');
  };

  const handleReceiveValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (numValue < 0) {
      setShowErrorModal(true);
      return;
    }

    if (numValue / exchangeRate > 1000) {
      setShowErrorModal(true);
      setSendValue('1000');
      setReceiveValue(String(1000 * exchangeRate));
      return;
    }

    setReceiveValue(value);
    setSendValue(value ? String(numValue / exchangeRate) : '');
  };

  const isValuesEmpty =
    !sendValue ||
    !receiveValue ||
    Number(sendValue) === 0 ||
    Number(receiveValue) === 0;

  const handleSwap = () => {
    if (!isValuesEmpty) {
      setShowSuccessModal(true);
    }
  };

  const handlers = useSwipeable({
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  // Обработчик фокуса на поле ввода - мягкая прокрутка только если инпут не виден
  const handleInputFocus = (
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    // Даем время клавиатуре появиться
    setTimeout(() => {
      if (inputRef.current && scrollContainerRef.current) {
        const inputRect = inputRef.current.getBoundingClientRect();
        const viewportHeight =
          window.visualViewport?.height || window.innerHeight;

        // Проверяем, виден ли инпут (с небольшим отступом сверху)
        const isInputVisible =
          inputRect.top >= 50 && inputRect.bottom <= viewportHeight - 50;

        // Прокручиваем только если инпут не виден или перекрыт клавиатурой
        if (!isInputVisible || inputRect.bottom > viewportHeight - 100) {
          // Используем 'start' вместо 'center' для более мягкой прокрутки
          // Это позволяет пользователю прокрутить дальше к кнопке
          inputRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    }, 300);
  };

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = threePracticaLocales[lang][2];

  return (
    <div {...handlers}>
      <motion.div
        key="threePractica2"
        initial="enter"
        animate="center"
        exit="exit"
        variants={slideVariants}
        transition={transition}
        style={{
          position: 'fixed',
          width: '100%',
          height: '100vh',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
        }}
      >
        <Container>
          <ScrollContainer ref={scrollContainerRef}>
            <HomeButton onClick={toHome}>
              <HomeButtonIcon src="/assets/homeIconPurple.svg" />
            </HomeButton>
            <Header>
              <Title>{t.title}</Title>
              <PracticaButton>{t.practica}</PracticaButton>
            </Header>
            <QuestionBlock>
              <QuestionText>{t.question}</QuestionText>
            </QuestionBlock>
            <SectionText>{t.description}</SectionText>
            <SwapContainer>
              <SwapInnerContainer>
                <SwapRowContainer>
                  <BalanceRow>
                    <TokenLabel>{t.token1Label || 'Token 1'}</TokenLabel>
                    <BalanceText onClick={() => {
                      const maxBalance = Math.min(Number(t.balanceA), 1000);
                      setSendValue(String(maxBalance));
                      setReceiveValue(String(maxBalance * exchangeRate));
                    }}>
                      <img
                        src="/assets/wallet.svg"
                        alt="wallet"
                        style={{ width: '12px', height: '12px' }}
                      />
                      {t.balanceA}
                    </BalanceText>
                  </BalanceRow>
                  <SwapRow>
                    <TokenInfo>
                      <TokenLogo>A</TokenLogo>
                      <SwapValue>{t.tokenA}</SwapValue>
                    </TokenInfo>
                    <Input
                      ref={sendInputRef}
                      type="number"
                      placeholder={t.inputPlaceholder}
                      value={sendValue}
                      isZero={!sendValue}
                      onChange={handleSendValueChange}
                      onFocus={() => handleInputFocus(sendInputRef)}
                      maxLength={6}
                    />
                  </SwapRow>
                </SwapRowContainer>

                <SwapIconContainer>
                  <img
                    src="/assets/plus.svg"
                    alt="plus"
                    style={{ width: '32px', height: '32px' }}
                  />
                </SwapIconContainer>

                <SwapRowContainer>
                  <BalanceRow>
                    <TokenLabel>{t.token2Label || 'Token 2'}</TokenLabel>
                    <BalanceText onClick={() => {
                      const maxBalanceB = Number(t.balanceB);
                      const equivalentA = maxBalanceB / exchangeRate;
                      const cappedA = Math.min(equivalentA, 1000);
                      setSendValue(String(cappedA));
                      setReceiveValue(String(cappedA * exchangeRate));
                    }}>
                      <img
                        src="/assets/wallet.svg"
                        alt="wallet"
                        style={{ width: '12px', height: '12px' }}
                      />
                      {t.balanceB}
                    </BalanceText>
                  </BalanceRow>
                  <SwapRow>
                    <TokenInfo>
                      <TokenLogo>B</TokenLogo>
                      <SwapValue>{t.tokenB}</SwapValue>
                    </TokenInfo>
                    <Input
                      ref={receiveInputRef}
                      type="number"
                      placeholder={t.inputPlaceholder}
                      value={receiveValue}
                      isZero={!receiveValue}
                      onChange={handleReceiveValueChange}
                      onFocus={() => handleInputFocus(receiveInputRef)}
                      maxLength={6}
                    />
                  </SwapRow>
                </SwapRowContainer>
              </SwapInnerContainer>

              <InputButton onClick={handleSwap} isDisabled={isValuesEmpty}>
                {isValuesEmpty ? t.inputEmpty : t.provideButton}
              </InputButton>
            </SwapContainer>
            <Footer practica textLeft={t.back} onSwipeRight={onSwipeRight} />
          </ScrollContainer>
        </Container>
      </motion.div>

      <AnimatePresence>
        {showErrorModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowErrorModal(false)}
          >
            <ErrorModalContent
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <CloseButton onClick={() => setShowErrorModal(false)} />
              <ModalTitle>{t.errorTitle}</ModalTitle>
              <ModalText>{t.errorText}</ModalText>
              <InputButton
                onClick={() => setShowErrorModal(false)}
                isDisabled={false}
              >
                {t.errorButton}
              </InputButton>
            </ErrorModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccessModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSuccessModal(false)}
          >
            <SuccessModalContent
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <CloseButton onClick={() => setShowSuccessModal(false)} />
              <ModalTitle>{t.successModal.title}</ModalTitle>
              <ModalText>{t.successModal.text}</ModalText>
              <ActionButton
                onClick={() => {
                  setShowSuccessModal(false);
                  setTimeout(() => {
                    onNavigateToPractica3?.();
                  }, 200);
                }}
              >
                {t.successModal.continue}
              </ActionButton>
            </SuccessModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThreePractica2;
